import React, { forwardRef, useCallback } from 'react';
import type { ForwardedRef, ReactNode } from 'react';

import { useFormContext } from 'react-hook-form';

import type { RequiredPopoverProps, PopoverProps } from '@vlabs/monokit/shared/ui/popovers/';

import { PopoverWithButton } from '../popover-with-button';

export interface FilterPopoverProps<TName extends string | string[]> extends PopoverProps, RequiredPopoverProps {
  name: TName;
  title?: ReactNode | undefined;
}

function FilterPopover<TName extends string | string[]>(
  {
    name,
    children,
    ...props
  }: FilterPopoverProps<TName>,
  ref: ForwardedRef<HTMLDivElement>,
): React.JSX.Element {
  const { watch, resetField } = useFormContext();

  const isEmptyValue = useCallback((value: unknown): boolean => {
    if (value === undefined) return true;
    if (Array.isArray(value)) return value.length === 0;
    return !value;
  }, []);

  // @ts-expect-error здесь ts ругается потому что не понимает что name может быть массивом
  const fieldValue = watch(name);

  const isResetButtonDisabled = Array.isArray(name)
    ? fieldValue.every((val) => isEmptyValue(val))
    : isEmptyValue(fieldValue);

  const handleClick = useCallback(() => {
    if (Array.isArray(name)) {
      name.forEach((n) => resetField(n));
      return;
    }
    resetField(name);
  }, [name, resetField]);

  return (
    <PopoverWithButton
      isButtonDisabled={isResetButtonDisabled}
      onClickReset={handleClick}
      ref={ref}
      {...props}
    >
      {children}
    </PopoverWithButton>
  );
}

const FilterPopoverWithRef = forwardRef(FilterPopover);

export { FilterPopoverWithRef as FilterPopover };
